import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, RefreshCw, Play, Check, MessageSquare, XCircle,
  Trash2, RotateCcw, Filter, Loader2, Zap, ChevronRight,
  Edit3, History, Archive, Film,
} from 'lucide-react';
import ProductionPanel from './production/ProductionPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  { key: 'detectado',      label: 'Detectado' },
  { key: 'en_produccion',  label: 'En producción' },
  { key: 'listo_revisar',  label: 'Listo para revisar' },
  { key: 'aprobado',       label: 'Aprobado' },
  { key: 'completado',     label: 'Completado' },
  { key: 'fallido',        label: 'Fallido' },
];

const STATE_LABEL = {
  detected: 'Detectado', proposed: 'Propuesto',
  working: 'Trabajando', draft_ready: 'Borrador listo',
  changes_requested: 'Cambios solicitados', approved: 'Aprobado',
  executing: 'Ejecutando', completed: 'Completado',
  rejected: 'Rechazado', failed: 'Fallido',
};

const PROJECT_LABEL = {
  mazal_kids: 'Mazal Kids',
  ultimo_testigo: 'El Último Testigo',
  alivan_self: 'AliVan',
};

// ---- History strip inline for cards ----------------------------------
const HistoryStrip = ({ history }) => {
  if (!history || history.length === 0) return null;
  const ICON = {
    proposed: '•', started: '▸', completed: '✓',
    approved: '✓', rejected: '✗', changes_requested: '⟲',
    failed: '⚠', blocked: '⛔', executed: '▶',
  };
  return (
    <div data-testid="wc-history-strip" className="mt-2 flex flex-wrap gap-1 text-[10px] text-white/50">
      {history.map((h, i) => (
        <span key={i} className="border border-white/10 rounded px-1.5 py-0.5" title={h.at}>
          {ICON[h.action] || '•'} {h.label}
        </span>
      ))}
    </div>
  );
};


// ---- Structured Draft editor -----------------------------------------
const DRAFT_FIELD_HINTS = {
  title: 'Título',
  concept: 'Concepto / Idea principal',
  hypothesis: 'Hipótesis',
  objective: 'Objetivo',
  description: 'Descripción',
  content: 'Contenido / Texto principal',
  narration: 'Narración',
  hook: 'Gancho inicial',
  hebrew: 'Texto en hebreo',
  phonetic: 'Transliteración fonética',
  notes: 'Observaciones',
  summary: 'Resumen (top-level)',
};
const LIST_FIELDS = new Set([
  'storyboard', 'scenes', 'sources', 'facts', 'candidates_considered',
  'unconfirmed_or_disputed', 'visual_assets_needed', 'hashtags',
]);


const DraftEditor = ({ job, onCancel, onSave }) => {
  const art = (job.artifacts && job.artifacts[job.artifacts.length - 1]) || null;
  const deliv = art?.deliverables || {};
  const [fields, setFields] = useState(() => {
    const f = { ...deliv };
    if (art?.summary) f.summary = art.summary;
    return f;
  });
  const [rawJson, setRawJson] = useState(JSON.stringify(deliv, null, 2));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const setKey = (k, v) => setFields(prev => ({ ...prev, [k]: v }));

  const removeKey = (k) => setFields(prev => {
    const { [k]: _drop, ...rest } = prev;
    return rest;
  });

  const commit = async () => {
    setSaving(true); setError('');
    try {
      let patch = { ...fields };
      if (showAdvanced) {
        // Advanced mode overrides — parse JSON and merge
        try {
          const parsed = JSON.parse(rawJson);
          patch = { ...patch, ...parsed };
        } catch (_e) {
          setError('JSON avanzado inválido');
          setSaving(false);
          return;
        }
      }
      // Never send an empty patch; strip undefined
      Object.keys(patch).forEach(k => {
        if (patch[k] === undefined) delete patch[k];
      });
      await onSave(patch);
    } finally {
      setSaving(false);
    }
  };

  // Ordered display: known hints first, LIST_FIELDS as textareas, everything else alphabetical.
  const knownKeys = Object.keys(DRAFT_FIELD_HINTS).filter(k => k in fields);
  const listKeys = [...LIST_FIELDS].filter(k => k in fields);
  const otherKeys = Object.keys(fields)
    .filter(k => !DRAFT_FIELD_HINTS[k] && !LIST_FIELDS.has(k))
    .sort();

  const renderField = (k) => {
    const v = fields[k];
    const label = DRAFT_FIELD_HINTS[k] || k;
    if (Array.isArray(v)) {
      const asText = v.map(x => typeof x === 'string' ? x : JSON.stringify(x)).join('\n');
      return (
        <div key={k} className="mb-3">
          <label className="text-[10px] font-jetbrains text-neon-red/80 uppercase tracking-widest">{label} <span className="text-white/30">(uno por línea)</span></label>
          <textarea
            data-testid={`wc-edit-${k}`}
            value={asText}
            onChange={(e) => setKey(k, e.target.value.split('\n').filter(l => l.trim().length))}
            rows={Math.min(8, Math.max(3, v.length + 1))}
            className="w-full mt-1 bg-black/60 border border-white/20 rounded p-2 text-xs text-white font-mono"
          />
        </div>
      );
    }
    if (v && typeof v === 'object') {
      // Non-list object → keep in JSON only; skip visual field but list a note
      return (
        <div key={k} className="mb-3 text-[11px] text-white/40 border border-white/10 rounded p-2">
          <span className="text-neon-red/80 uppercase font-jetbrains text-[10px] mr-2">{label}</span>
          objeto anidado — editable en la sección avanzada.
        </div>
      );
    }
    const isMultiline = typeof v === 'string' && (v.length > 80 || v.includes('\n'));
    return (
      <div key={k} className="mb-3">
        <div className="flex items-center justify-between">
          <label className="text-[10px] font-jetbrains text-neon-red/80 uppercase tracking-widest">{label}</label>
          <button onClick={() => removeKey(k)} className="text-[9px] text-white/30 hover:text-red-400">quitar campo</button>
        </div>
        {isMultiline ? (
          <textarea
            data-testid={`wc-edit-${k}`}
            value={v ?? ''} onChange={(e) => setKey(k, e.target.value)}
            rows={4}
            className="w-full mt-1 bg-black/60 border border-white/20 rounded p-2 text-xs text-white"
          />
        ) : (
          <input
            data-testid={`wc-edit-${k}`}
            value={v ?? ''} onChange={(e) => setKey(k, e.target.value)}
            className="w-full mt-1 bg-black/60 border border-white/20 rounded p-2 text-xs text-white"
          />
        )}
      </div>
    );
  };

  return (
    <div data-testid="wc-draft-editor" className="border border-neon-red/30 bg-black/40 rounded p-3 mt-3">
      <div className="text-neon-red/90 text-[10px] font-jetbrains uppercase tracking-widest mb-3">
        Editar borrador (no invoca al agente ni consume una generación)
      </div>
      {knownKeys.map(renderField)}
      {listKeys.map(renderField)}
      {otherKeys.map(renderField)}
      <div className="mt-3">
        <button
          data-testid="wc-toggle-advanced"
          onClick={() => setShowAdvanced(s => !s)}
          className="text-[10px] text-white/60 hover:text-white underline"
        >
          {showAdvanced ? '▼ Ocultar JSON avanzado' : '▶ Mostrar JSON avanzado (opcional)'}
        </button>
        {showAdvanced && (
          <textarea
            data-testid="wc-edit-json"
            value={rawJson} onChange={(e) => setRawJson(e.target.value)}
            rows={10}
            className="w-full mt-2 bg-black/70 border border-white/10 rounded p-2 text-[11px] text-white font-mono"
          />
        )}
      </div>
      {error && <div className="text-[11px] text-red-300 mt-2">{error}</div>}
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onCancel} data-testid="wc-edit-cancel" className="text-[11px] px-3 py-1 border border-white/20 text-white/60 rounded">Cancelar</button>
        <button
          data-testid="wc-edit-save" onClick={commit} disabled={saving}
          className="text-[11px] px-3 py-1 border border-neon-red/50 text-neon-red hover:bg-neon-red/20 rounded"
        >
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
};


// ---- Job card ---------------------------------------------------------
const JobCard = ({ job, selected, onToggleSelect, onAction, onOpen, historyStrip }) => {
  const art = (job.artifacts && job.artifacts[0]) || null;
  return (
    <div
      data-testid={`wc-job-card-${job.job_id}`}
      className="border border-neon-red/20 bg-black/30 rounded p-3 mb-2 hover:border-neon-red/50 transition-colors"
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          data-testid={`wc-job-checkbox-${job.job_id}`}
          checked={selected}
          onChange={onToggleSelect}
          className="mt-1 accent-red-500"
        />
        <div className="flex-1 min-w-0" onClick={onOpen} role="button">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-jetbrains uppercase tracking-widest text-neon-red/80">
              {PROJECT_LABEL[job.project_slug] || job.project_slug}
            </span>
            <span className="text-[10px] font-jetbrains text-white/40">
              {STATE_LABEL[job.state] || job.state}
            </span>
          </div>
          <div className="text-sm text-white font-medium mb-1 truncate">{job.topic}</div>
          {job.reason && (
            <div className="text-[11px] text-white/50 mb-1 line-clamp-2">{job.reason}</div>
          )}
          {art?.summary && (
            <div className="text-[11px] text-emerald-300/80 mb-1 line-clamp-2">
              → {art.summary}
            </div>
          )}
          <div className="text-[10px] text-white/40">
            agente: {job.agent_id || '—'} · {new Date(job.updated_at).toLocaleString()}
          </div>
          {job.errors && job.errors.length > 0 && (
            <div className="text-[11px] text-red-400 mt-1">
              ⚠ {job.errors[job.errors.length - 1].message}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {job.state === 'proposed' && (
          <button
            data-testid={`wc-run-agent-${job.job_id}`}
            onClick={() => onAction('run-agent', job)}
            className="text-[10px] px-2 py-1 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20 rounded"
          >
            <Zap className="inline w-3 h-3 mr-1" />Ejecutar agente
          </button>
        )}
        {(job.state === 'draft_ready' || job.state === 'changes_requested') && (
          <>
            <button
              data-testid={`wc-edit-draft-${job.job_id}`}
              onClick={() => onAction('edit-draft', job)}
              className="text-[10px] px-2 py-1 border border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20 rounded"
            >
              <Edit3 className="inline w-3 h-3 mr-1" />Editar borrador
            </button>
            <button
              data-testid={`wc-approve-${job.job_id}`}
              onClick={() => onAction('approve', job)}
              className="text-[10px] px-2 py-1 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20 rounded"
            >
              <Check className="inline w-3 h-3 mr-1" />Aprobar
            </button>
            <button
              data-testid={`wc-changes-${job.job_id}`}
              onClick={() => onAction('changes', job)}
              className="text-[10px] px-2 py-1 border border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/20 rounded"
            >
              <MessageSquare className="inline w-3 h-3 mr-1" />Solicitar cambios
            </button>
            <button
              data-testid={`wc-reject-${job.job_id}`}
              onClick={() => onAction('reject', job)}
              className="text-[10px] px-2 py-1 border border-red-500/50 text-red-300 hover:bg-red-500/20 rounded"
            >
              <XCircle className="inline w-3 h-3 mr-1" />Rechazar
            </button>
          </>
        )}
        {job.state === 'changes_requested' && (
          <button
            data-testid={`wc-rerun-${job.job_id}`}
            onClick={() => onAction('run-agent', job)}
            className="text-[10px] px-2 py-1 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20 rounded"
          >
            <Play className="inline w-3 h-3 mr-1" />Re-ejecutar agente
          </button>
        )}
        {job.state === 'approved' && (
          <button
            data-testid={`wc-execute-${job.job_id}`}
            onClick={() => onAction('execute', job)}
            className="text-[10px] px-2 py-1 border border-neon-red/50 text-neon-red hover:bg-neon-red/20 rounded"
          >
            <Play className="inline w-3 h-3 mr-1" />Ejecutar
          </button>
        )}
        <button
          data-testid={`wc-delete-${job.job_id}`}
          onClick={() => onAction('delete', job)}
          className="text-[10px] px-2 py-1 border border-white/20 text-white/60 hover:bg-white/10 rounded"
        >
          <Trash2 className="inline w-3 h-3 mr-1" />Eliminar
        </button>
      </div>
      <HistoryStrip history={historyStrip} />
    </div>
  );
};


// ---- Job detail modal --------------------------------------------------
const JobDetailModal = ({ job, onClose }) => {
  if (!job) return null;
  const art = (job.artifacts && job.artifacts[0]) || null;
  const deliv = art?.deliverables || {};
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="wc-detail-backdrop"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-[#0A0A0A] border border-neon-red/40 rounded max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="wc-detail-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-neon-red/20 sticky top-0 bg-[#0A0A0A]">
          <div>
            <div className="text-[10px] font-jetbrains uppercase tracking-widest text-neon-red">
              {PROJECT_LABEL[job.project_slug] || job.project_slug} · {STATE_LABEL[job.state]}
            </div>
            <div className="text-white font-outfit text-lg">{job.topic}</div>
          </div>
          <button onClick={onClose} data-testid="wc-detail-close" className="text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <section>
            <div className="text-neon-red/80 text-[10px] font-jetbrains uppercase tracking-widest mb-1">Objetivo</div>
            <div className="text-white/90">{job.objective}</div>
          </section>
          <section>
            <div className="text-neon-red/80 text-[10px] font-jetbrains uppercase tracking-widest mb-1">Motivo de la decisión</div>
            <div className="text-white/70 text-xs">{job.reason}</div>
          </section>
          <section>
            <div className="text-neon-red/80 text-[10px] font-jetbrains uppercase tracking-widest mb-1">Evidencias utilizadas</div>
            <ul className="text-white/70 text-xs list-disc list-inside">
              {(job.evidence || []).map((e, i) => (
                <li key={i}>{e.type}: {JSON.stringify(e.value ?? e.count ?? e.present)}</li>
              ))}
            </ul>
          </section>
          {art && (
            <section>
              <div className="text-neon-red/80 text-[10px] font-jetbrains uppercase tracking-widest mb-1">
                Borrador del agente ({art.agent_id})
              </div>
              {art.summary && <div className="text-emerald-300 mb-2">{art.summary}</div>}
              {Object.keys(deliv).length > 0 && (
                <pre data-testid="wc-detail-deliverables" className="text-[11px] text-white/70 bg-black/50 border border-neon-red/10 p-3 overflow-x-auto whitespace-pre-wrap max-h-96">
                  {JSON.stringify(deliv, null, 2)}
                </pre>
              )}
            </section>
          )}
          {(job.changes_requested || []).length > 0 && (
            <section>
              <div className="text-neon-red/80 text-[10px] font-jetbrains uppercase tracking-widest mb-1">Cambios solicitados</div>
              <ul className="text-yellow-300/80 text-xs">
                {job.changes_requested.map((c, i) => (
                  <li key={i}>• {c.comment} <span className="text-white/40">({new Date(c.at).toLocaleString()})</span></li>
                ))}
              </ul>
            </section>
          )}
          {(job.approvals || []).length > 0 && (
            <section>
              <div className="text-neon-red/80 text-[10px] font-jetbrains uppercase tracking-widest mb-1">Aprobaciones</div>
              <ul className="text-emerald-300/80 text-xs">
                {job.approvals.map((a, i) => (
                  <li key={i}>• {a.comment || '(sin comentario)'} <span className="text-white/40">({new Date(a.at).toLocaleString()})</span></li>
                ))}
              </ul>
            </section>
          )}
          {(job.errors || []).length > 0 && (
            <section>
              <div className="text-neon-red/80 text-[10px] font-jetbrains uppercase tracking-widest mb-1">Errores</div>
              <ul className="text-red-300/80 text-xs">
                {job.errors.map((e, i) => (
                  <li key={i}>• {e.message}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};


// ---- Comment prompt modal ---------------------------------------------
const CommentModal = ({ title, placeholder, onCancel, onConfirm, confirmLabel = 'Confirmar' }) => {
  const [text, setText] = useState('');
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="wc-comment-modal"
    >
      <motion.div
        initial={{ scale: 0.95 }} animate={{ scale: 1 }}
        className="bg-[#0A0A0A] border border-neon-red/40 rounded max-w-md w-full p-5"
      >
        <div className="text-white font-outfit text-base mb-3">{title}</div>
        <textarea
          data-testid="wc-comment-input"
          value={text} onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className="w-full bg-black/60 border border-neon-red/20 rounded p-2 text-sm text-white"
        />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onCancel} data-testid="wc-comment-cancel" className="text-[11px] px-3 py-1 border border-white/20 text-white/60 rounded">Cancelar</button>
          <button
            data-testid="wc-comment-confirm"
            onClick={() => onConfirm(text)}
            className="text-[11px] px-3 py-1 border border-neon-red/50 text-neon-red hover:bg-neon-red/20 rounded"
          >
            {confirmLabel}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};


// ---- Confirm modal (delete etc.) ---------------------------------------
const ConfirmModal = ({ title, body, onCancel, onConfirm, confirmLabel = 'Confirmar', danger = false }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    data-testid="wc-confirm-modal"
  >
    <motion.div
      initial={{ scale: 0.95 }} animate={{ scale: 1 }}
      className="bg-[#0A0A0A] border border-neon-red/40 rounded max-w-md w-full p-5"
    >
      <div className="text-white font-outfit text-base mb-2">{title}</div>
      <div className="text-white/70 text-sm mb-4">{body}</div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} data-testid="wc-confirm-cancel" className="text-[11px] px-3 py-1 border border-white/20 text-white/60 rounded">Cancelar</button>
        <button
          data-testid="wc-confirm-ok"
          onClick={onConfirm}
          className={`text-[11px] px-3 py-1 border rounded ${danger
            ? 'border-red-500/50 text-red-300 hover:bg-red-500/20'
            : 'border-neon-red/50 text-neon-red hover:bg-neon-red/20'}`}
        >
          {confirmLabel}
        </button>
      </div>
    </motion.div>
  </motion.div>
);


// ============================================================================
// Main Work Center Panel
// ============================================================================
const WorkCenterPanel = ({ open, onClose }) => {
  const [sections, setSections] = useState({});
  const [trash, setTrash] = useState([]);
  const [legacyJobs, setLegacyJobs] = useState([]);
  const [legacyTotal, setLegacyTotal] = useState(0);
  const [legacyTrash, setLegacyTrash] = useState([]);
  const [legacyOffset, setLegacyOffset] = useState(0);
  const [legacyFilter, setLegacyFilter] = useState({ status: '', project: '' });
  const [view, setView] = useState('panel');  // panel | trash | history
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [selectedLegacy, setSelectedLegacy] = useState(new Set());
  const [detailJob, setDetailJob] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [pending, setPending] = useState(null);   // {action, job}
  const [projectFilter, setProjectFilter] = useState('');
  const [limitPerSection, setLimitPerSection] = useState(20);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [activeWorking, setActiveWorking] = useState(0);
  const [historyByJob, setHistoryByJob] = useState({});
  const autoRefreshTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams();
      if (projectFilter) qs.set('project_slug', projectFilter);
      qs.set('limit_per_section', String(limitPerSection));
      const [pRes, tRes] = await Promise.all([
        fetch(`${API}/work-center/panel?${qs}`, { credentials: 'include' }),
        fetch(`${API}/work-center/trash`, { credentials: 'include' }),
      ]);
      if (pRes.ok) {
        const j = await pRes.json();
        setSections(j.sections || {});
        setActiveWorking(j.active_working || 0);
        // Prefetch history strips for the visible jobs (short 3-item summary)
        const allJobs = Object.values(j.sections || {}).flat();
        const strips = {};
        await Promise.all(allJobs.map(async (job) => {
          try {
            const h = await fetch(`${API}/work-center/jobs/${job.job_id}/history`,
              { credentials: 'include' });
            if (h.ok) {
              const hj = await h.json();
              strips[job.job_id] = (hj.history || []).slice(-5);
            }
          } catch (_e) { /* non-fatal */ }
        }));
        setHistoryByJob(strips);
      }
      if (tRes.ok) setTrash((await tRes.json()).trash || []);
    } catch (e) {
      setError('No se pudo cargar el panel');
    } finally {
      setLoading(false);
    }
  }, [projectFilter, limitPerSection]);

  const loadLegacy = useCallback(async () => {
    const qs = new URLSearchParams({
      limit: '15', offset: String(legacyOffset),
    });
    if (legacyFilter.project) qs.set('project_slug', legacyFilter.project);
    if (legacyFilter.status) qs.set('status', legacyFilter.status);
    try {
      const [jRes, tRes] = await Promise.all([
        fetch(`${API}/jobs?${qs}`, { credentials: 'include' }),
        fetch(`${API}/jobs-trash`, { credentials: 'include' }),
      ]);
      if (jRes.ok) {
        const j = await jRes.json();
        setLegacyJobs(j.jobs || []);
        setLegacyTotal(j.total || 0);
      }
      if (tRes.ok) setLegacyTrash((await tRes.json()).trash || []);
    } catch (_e) { /* non-fatal */ }
  }, [legacyOffset, legacyFilter]);

  useEffect(() => { if (open) load(); }, [open, load]);
  // Load legacy on open so the Papelera count badge is accurate before user visits Historial/Trash.
  useEffect(() => { if (open) loadLegacy(); }, [open, view, loadLegacy]);

  // Auto-refresh only while agents are working. Poll every 15s.
  useEffect(() => {
    if (autoRefreshTimer.current) {
      clearInterval(autoRefreshTimer.current);
      autoRefreshTimer.current = null;
    }
    if (open && activeWorking > 0) {
      autoRefreshTimer.current = setInterval(() => load(), 15000);
    }
    return () => {
      if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
    };
  }, [open, activeWorking, load]);

  const runCycle = async () => {
    setRunning(true); setError('');
    try {
      const r = await fetch(`${API}/work-center/initiative/run`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: false }),
      });
      const j = await r.json();
      setNotice(`Ciclo ejecutado: ${(j.created || []).length} propuestas nuevas, ${(j.duplicates || []).length} duplicadas.`);
      setTimeout(() => setNotice(''), 4000);
      await load();
    } catch (e) {
      setError('Fallo al ejecutar el ciclo');
    } finally {
      setRunning(false);
    }
  };

  const toggleSelect = (job_id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(job_id)) s.delete(job_id); else s.add(job_id);
      return s;
    });
  };

  const httpPost = async (path, body = {}) => {
    const r = await fetch(`${API}${path}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return { ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) };
  };

  const runAgent = async (job) => {
    setNotice(`Invocando agente ${job.agent_id}... esto puede tardar hasta ~60s`);
    // Fire-and-forget with polling: the LLM call can exceed the 60s Cloudflare ingress
    const controller = new AbortController();
    const kill = setTimeout(() => controller.abort(), 3000);
    try {
      await fetch(`${API}/work-center/jobs/${job.job_id}/run-agent`, {
        method: 'POST', credentials: 'include', signal: controller.signal,
      });
    } catch (_e) { /* expected on abort */ }
    finally { clearTimeout(kill); }
    // Poll for up to 120s (Claude on Mazal Kids can take ~100s)
    for (let i = 0; i < 40; i++) {
      await new Promise(r => setTimeout(r, 3000));
      const r = await fetch(`${API}/work-center/jobs/${job.job_id}`, { credentials: 'include' });
      if (r.ok) {
        const jd = (await r.json()).job;
        if (jd.state === 'draft_ready' || jd.state === 'failed') {
          await load();
          setNotice(jd.state === 'draft_ready'
            ? `Borrador listo: ${jd.artifacts?.[0]?.summary || 'entregado'}`
            : `El agente falló: ${jd.errors?.[jd.errors.length-1]?.message || 'error'}`);
          setTimeout(() => setNotice(''), 6000);
          return;
        }
      }
    }
    setNotice('El agente sigue ejecutándose en segundo plano. Refresca en un momento.');
    setTimeout(() => setNotice(''), 6000);
    await load();
  };

  const handleAction = async (action, job) => {
    if (action === 'run-agent') return runAgent(job);
    if (action === 'edit-draft') return setEditingJob(job);
    if (action === 'approve') return setPending({ action, job });
    if (action === 'changes') return setPending({ action, job });
    if (action === 'reject')  return setPending({ action, job });
    if (action === 'delete')  return setPending({ action, job });
    if (action === 'execute') {
      const { ok, data } = await httpPost(`/work-center/jobs/${job.job_id}/execute`,
        { user_confirmed: false });
      if (!ok) setError(data.detail || 'No se pudo ejecutar');
      await load();
    }
  };

  const saveDraftEdits = async (patch) => {
    if (!editingJob) return;
    const r = await fetch(`${API}/work-center/jobs/${editingJob.job_id}/edit-draft`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ patch }),
    });
    if (r.ok) {
      setNotice('Borrador editado (sin invocar al agente).');
      setTimeout(() => setNotice(''), 3000);
      setEditingJob(null);
      await load();
    } else {
      const d = await r.json().catch(() => ({}));
      setError(d.detail || 'No se pudo guardar la edición');
    }
  };

  const confirmPending = async (comment) => {
    if (!pending) return;
    const { action, job } = pending;
    let path = '', body = {};
    if (action === 'approve') { path = `/work-center/jobs/${job.job_id}/approve`; body = { comment: comment || null }; }
    if (action === 'changes') {
      if (!comment || !comment.trim()) { setError('Comentario requerido'); return; }
      path = `/work-center/jobs/${job.job_id}/request-changes`; body = { comment };
    }
    if (action === 'reject') { path = `/work-center/jobs/${job.job_id}/reject`; body = { comment: comment || null }; }
    if (action === 'delete') {
      const r = await fetch(`${API}/work-center/jobs/${job.job_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      setPending(null);
      if (r.ok) { setNotice('Movido a la papelera.'); setTimeout(() => setNotice(''), 3000); }
      await load(); return;
    }
    const { ok, data } = await httpPost(path, body);
    setPending(null);
    if (!ok) setError(data.detail || 'Error'); else setNotice(`Acción "${action}" aplicada`);
    setTimeout(() => setNotice(''), 3000);
    await load();
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = [...selected];
    const { ok } = await httpPost('/work-center/jobs/bulk-delete', { job_ids: ids });
    if (ok) { setSelected(new Set()); setNotice(`${ids.length} movidos a la papelera.`); setTimeout(() => setNotice(''), 3000); }
    await load();
  };

  const cleanCompleted = async () => {
    const body = projectFilter ? { project_slug: projectFilter } : {};
    const { ok, data } = await httpPost('/work-center/jobs/clean-completed', body);
    if (ok) { setNotice(`${data.deleted} completados movidos a la papelera.`); setTimeout(() => setNotice(''), 3000); }
    await load();
  };

  const restore = async (job_id) => {
    await httpPost(`/work-center/trash/${job_id}/restore`);
    await load();
  };

  const [hardDeleteConfirm, setHardDeleteConfirm] = useState(null);
  const hardDelete = async () => {
    if (!hardDeleteConfirm) return;
    await fetch(`${API}/work-center/trash/${hardDeleteConfirm}?confirm=true`, {
      method: 'DELETE', credentials: 'include',
    });
    setHardDeleteConfirm(null);
    setNotice('Eliminado definitivamente.');
    setTimeout(() => setNotice(''), 3000);
    await load();
  };

  const [legacyHardDelete, setLegacyHardDelete] = useState(null);
  const legacyHardDeleteConfirm = async () => {
    if (!legacyHardDelete) return;
    await fetch(`${API}/jobs-trash/${legacyHardDelete}?confirm=true`, {
      method: 'DELETE', credentials: 'include',
    });
    setLegacyHardDelete(null);
    setNotice('Trabajo legacy eliminado definitivamente.');
    setTimeout(() => setNotice(''), 3000);
    await loadLegacy();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={onClose} data-testid="wc-panel-backdrop"
          />
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed top-0 right-0 h-full w-full sm:w-[720px] bg-[#0A0A0A] border-l border-neon-red/30 z-50 flex flex-col"
            data-testid="wc-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-neon-red/20">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-neon-red" />
                <div>
                  <h2 className="font-outfit text-lg font-bold text-white">Centro de Trabajo</h2>
                  <p className="font-jetbrains text-[10px] text-white/50 tracking-widest uppercase">
                    Iniciativa de AliVan con aprobación humana
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="wc-refresh"
                  onClick={load} disabled={loading}
                  className="p-2 text-white/60 hover:text-neon-red"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button data-testid="wc-close" onClick={onClose} className="p-2 text-white/60 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="px-4 py-2 border-b border-neon-red/10 space-y-2">
              <div className="flex items-center flex-wrap gap-2">
                <button
                  data-testid="wc-run-cycle"
                  onClick={runCycle} disabled={running}
                  className="text-[11px] px-3 py-1.5 border border-neon-red/50 text-neon-red hover:bg-neon-red/20 rounded flex items-center gap-1"
                >
                  {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                  Ejecutar ciclo de iniciativa
                </button>
                <button
                  data-testid="wc-view-panel"
                  onClick={() => setView('panel')}
                  className={`text-[11px] px-2 py-1 border rounded ${view === 'panel' ? 'border-neon-red text-neon-red' : 'border-white/20 text-white/60'}`}
                >Panel</button>
                <button
                  data-testid="wc-view-history"
                  onClick={() => setView('history')}
                  className={`text-[11px] px-2 py-1 border rounded ${view === 'history' ? 'border-neon-red text-neon-red' : 'border-white/20 text-white/60'}`}
                >
                  <Archive className="inline w-3 h-3 mr-1" />Historial{activeWorking > 0 ? ` · ${activeWorking} activo(s)` : ''}
                </button>
                <button
                  data-testid="wc-view-trash"
                  onClick={() => setView('trash')}
                  className={`text-[11px] px-2 py-1 border rounded ${view === 'trash' ? 'border-neon-red text-neon-red' : 'border-white/20 text-white/60'}`}
                >Papelera ({trash.length + legacyTrash.length})</button>
                <button
                  data-testid="wc-view-production"
                  onClick={() => setView('production')}
                  className={`text-[11px] px-2 py-1 border rounded flex items-center gap-1 ${view === 'production' ? 'border-neon-red text-neon-red' : 'border-white/20 text-white/60'}`}
                >
                  <Film className="w-3 h-3" />Producción
                </button>
                <select
                  data-testid="wc-filter-project"
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  className="text-[11px] bg-black/40 border border-white/20 text-white/70 rounded px-2 py-1"
                >
                  <option value="">Todos los proyectos</option>
                  <option value="mazal_kids">Mazal Kids</option>
                  <option value="ultimo_testigo">El Último Testigo</option>
                </select>
                <select
                  data-testid="wc-filter-limit"
                  value={limitPerSection}
                  onChange={(e) => setLimitPerSection(Number(e.target.value))}
                  className="text-[11px] bg-black/40 border border-white/20 text-white/70 rounded px-2 py-1"
                >
                  <option value={10}>10 por sección</option>
                  <option value={20}>20 por sección</option>
                  <option value={50}>50 por sección</option>
                </select>
              </div>
              {selected.size > 0 && (
                <div className="flex items-center gap-2 border-t border-neon-red/10 pt-2">
                  <span className="text-[10px] text-white/60">{selected.size} seleccionado(s)</span>
                  <button
                    data-testid="wc-bulk-delete"
                    onClick={bulkDelete}
                    className="text-[11px] px-2 py-1 border border-red-500/50 text-red-300 hover:bg-red-500/20 rounded"
                  >
                    <Trash2 className="inline w-3 h-3 mr-1" />Eliminar seleccionados
                  </button>
                  <button onClick={() => setSelected(new Set())} className="text-[10px] text-white/50">
                    limpiar selección
                  </button>
                </div>
              )}
              <div className="flex justify-end">
                <button
                  data-testid="wc-clean-completed"
                  onClick={cleanCompleted}
                  className="text-[10px] px-2 py-1 border border-white/20 text-white/50 hover:text-white/80 rounded"
                >
                  Limpiar completados{projectFilter ? ` (${PROJECT_LABEL[projectFilter]})` : ''}
                </button>
              </div>
              {notice && (
                <div data-testid="wc-notice" className="text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1">
                  {notice}
                </div>
              )}
              {error && (
                <div data-testid="wc-error" className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1">
                  {error}
                </div>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4">
              {view === 'panel' && (
                <>
                  {SECTIONS.map(({ key, label }) => {
                    const jobs = sections[key] || [];
                    return (
                      <div key={key} data-testid={`wc-section-${key}`} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="text-white font-outfit text-sm">{label}</div>
                          <div className="text-[10px] text-white/40">{jobs.length}</div>
                        </div>
                        {jobs.length === 0 ? (
                          <div className="text-[11px] text-white/30 italic">— vacío —</div>
                        ) : jobs.map(job => (
                          <JobCard
                            key={job.job_id}
                            job={job}
                            selected={selected.has(job.job_id)}
                            onToggleSelect={() => toggleSelect(job.job_id)}
                            onAction={handleAction}
                            onOpen={() => setDetailJob(job)}
                            historyStrip={historyByJob[job.job_id]}
                          />
                        ))}
                      </div>
                    );
                  })}
                </>
              )}
              {view === 'trash' && (
                <>
                  <div className="text-white/60 text-xs mb-3">
                    Los elementos aquí no aparecen en el panel principal. Puedes restaurarlos o eliminarlos definitivamente.
                  </div>
                  {trash.length === 0 && legacyTrash.length === 0 && <div className="text-[11px] text-white/30 italic">— papelera vacía —</div>}
                  {trash.map(job => (
                    <div key={job.job_id} data-testid={`wc-trash-item-${job.job_id}`} className="border border-white/10 bg-black/30 rounded p-3 mb-2">
                      <div className="text-[10px] text-white/40">Work Center · {PROJECT_LABEL[job.project_slug] || job.project_slug} · eliminado {new Date(job.deleted_at).toLocaleString()}</div>
                      <div className="text-sm text-white/80">{job.topic}</div>
                      <div className="mt-2 flex gap-2">
                        <button
                          data-testid={`wc-restore-${job.job_id}`}
                          onClick={() => restore(job.job_id)}
                          className="text-[10px] px-2 py-1 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20 rounded"
                        >
                          <RotateCcw className="inline w-3 h-3 mr-1" />Restaurar
                        </button>
                        <button
                          data-testid={`wc-hard-delete-${job.job_id}`}
                          onClick={() => setHardDeleteConfirm(job.job_id)}
                          className="text-[10px] px-2 py-1 border border-red-500/50 text-red-300 hover:bg-red-500/20 rounded"
                        >
                          Eliminar definitivamente
                        </button>
                      </div>
                    </div>
                  ))}
                  {legacyTrash.map(job => (
                    <div key={job.job_id} data-testid={`wc-legacy-trash-item-${job.job_id}`} className="border border-white/10 bg-black/30 rounded p-3 mb-2">
                      <div className="text-[10px] text-white/40">Historial · {PROJECT_LABEL[job.project_slug] || job.project_slug} · {job.status} · eliminado {new Date(job.deleted_at).toLocaleString()}</div>
                      <div className="text-sm text-white/80">{job.description || job.summary || job.title || 'trabajo'}</div>
                      <div className="mt-2 flex gap-2">
                        <button
                          data-testid={`wc-legacy-restore-${job.job_id}`}
                          onClick={async () => { await fetch(`${API}/jobs-trash/${job.job_id}/restore`, {method: 'POST', credentials: 'include'}); await loadLegacy(); }}
                          className="text-[10px] px-2 py-1 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20 rounded"
                        >
                          <RotateCcw className="inline w-3 h-3 mr-1" />Restaurar
                        </button>
                        <button
                          data-testid={`wc-legacy-hard-delete-${job.job_id}`}
                          onClick={() => setLegacyHardDelete(job.job_id)}
                          className="text-[10px] px-2 py-1 border border-red-500/50 text-red-300 hover:bg-red-500/20 rounded"
                        >
                          Eliminar definitivamente
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {view === 'production' && (
                <ProductionPanel />
              )}
              {view === 'history' && (
                <>
                  <div className="text-white/60 text-xs mb-3">
                    <Archive className="inline w-4 h-4 mr-1" />
                    Historial de trabajos (ejecuciones anteriores por agentes). Están separados del panel principal para no mezclarse con propuestas activas.
                  </div>
                  <div className="flex items-center flex-wrap gap-2 mb-3">
                    <select
                      data-testid="wc-legacy-filter-project"
                      value={legacyFilter.project}
                      onChange={(e) => { setLegacyFilter(f => ({...f, project: e.target.value})); setLegacyOffset(0); }}
                      className="text-[11px] bg-black/40 border border-white/20 text-white/70 rounded px-2 py-1"
                    >
                      <option value="">Todos los proyectos</option>
                      <option value="mazal_kids">Mazal Kids</option>
                      <option value="ultimo_testigo">El Último Testigo</option>
                    </select>
                    <select
                      data-testid="wc-legacy-filter-status"
                      value={legacyFilter.status}
                      onChange={(e) => { setLegacyFilter(f => ({...f, status: e.target.value})); setLegacyOffset(0); }}
                      className="text-[11px] bg-black/40 border border-white/20 text-white/70 rounded px-2 py-1"
                    >
                      <option value="">Todos los estados</option>
                      <option value="COMPLETED">Completados</option>
                      <option value="RUNNING">En ejecución</option>
                      <option value="FAILED">Fallidos</option>
                      <option value="PENDING">Pendientes</option>
                    </select>
                    {selectedLegacy.size > 0 && (
                      <button
                        data-testid="wc-legacy-bulk-delete"
                        onClick={async () => {
                          await fetch(`${API}/jobs/bulk-delete`, {method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({job_ids: [...selectedLegacy]})});
                          setSelectedLegacy(new Set());
                          await loadLegacy();
                        }}
                        className="text-[11px] px-2 py-1 border border-red-500/50 text-red-300 hover:bg-red-500/20 rounded"
                      >
                        <Trash2 className="inline w-3 h-3 mr-1" />Eliminar {selectedLegacy.size} seleccionado(s)
                      </button>
                    )}
                    <button
                      data-testid="wc-legacy-clean-completed"
                      onClick={async () => {
                        const body = legacyFilter.project ? {project_slug: legacyFilter.project} : {};
                        const r = await fetch(`${API}/jobs/clean-completed`, {method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)});
                        if (r.ok) {
                          const j = await r.json();
                          setNotice(`${j.deleted} completados movidos a la papelera.`);
                          setTimeout(() => setNotice(''), 3000);
                        }
                        await loadLegacy();
                      }}
                      className="text-[11px] px-2 py-1 border border-white/20 text-white/60 hover:text-white/80 rounded"
                    >
                      Limpiar completados
                    </button>
                  </div>
                  {legacyJobs.length === 0 && <div className="text-[11px] text-white/30 italic">— historial vacío —</div>}
                  {legacyJobs.map(job => (
                    <div key={job.job_id} data-testid={`wc-legacy-item-${job.job_id}`} className="border border-white/10 bg-black/30 rounded p-3 mb-2 flex items-start gap-2">
                      <input type="checkbox" data-testid={`wc-legacy-checkbox-${job.job_id}`}
                        checked={selectedLegacy.has(job.job_id)}
                        onChange={() => setSelectedLegacy(prev => { const s = new Set(prev); if (s.has(job.job_id)) s.delete(job.job_id); else s.add(job.job_id); return s; })}
                        className="mt-1 accent-red-500" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-white/40">
                          {PROJECT_LABEL[job.project_slug] || job.project_slug} · {job.status || 'unknown'} · {new Date(job.created_at || job.updated_at).toLocaleString()}
                        </div>
                        <div className="text-sm text-white/80 truncate">{job.description || job.summary || job.title || 'trabajo'}</div>
                        {job.agent_id && <div className="text-[10px] text-white/40">agente: {job.agent_id}</div>}
                      </div>
                      <button
                        data-testid={`wc-legacy-delete-${job.job_id}`}
                        onClick={async () => {
                          if (!window.confirm(`Mover a papelera: "${(job.description || job.title || 'trabajo').slice(0, 60)}"?`)) return;
                          await fetch(`${API}/jobs/${job.job_id}`, {method: 'DELETE', credentials: 'include'});
                          await loadLegacy();
                        }}
                        className="text-[10px] px-2 py-1 border border-white/20 text-white/60 hover:bg-white/10 rounded"
                      >
                        <Trash2 className="inline w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {legacyTotal > legacyJobs.length + legacyOffset && (
                    <button
                      data-testid="wc-legacy-load-more"
                      onClick={() => setLegacyOffset(o => o + 15)}
                      className="text-[11px] mt-2 w-full py-2 border border-white/10 text-white/50 hover:bg-white/5 rounded"
                    >
                      Cargar más ({legacyTotal - legacyJobs.length - legacyOffset} restantes)
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>

          {editingJob && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[75] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.95 }} animate={{ scale: 1 }}
                className="bg-[#0A0A0A] border border-neon-red/40 rounded max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-white font-outfit">Editar: {editingJob.topic}</div>
                  <button onClick={() => setEditingJob(null)} className="text-white/60"><X className="w-4 h-4" /></button>
                </div>
                <DraftEditor
                  job={editingJob}
                  onCancel={() => setEditingJob(null)}
                  onSave={saveDraftEdits}
                />
              </motion.div>
            </motion.div>
          )}

          {/* Modals */}
          {detailJob && <JobDetailModal job={detailJob} onClose={() => setDetailJob(null)} />}
          {pending && pending.action === 'delete' && (
            <ConfirmModal
              title="Mover a la papelera"
              body={`Se moverá "${pending.job.topic}" (${STATE_LABEL[pending.job.state]}) a la papelera. Podrás restaurarlo o eliminarlo definitivamente después.`}
              danger
              confirmLabel="Mover a papelera"
              onCancel={() => setPending(null)}
              onConfirm={() => confirmPending(null)}
            />
          )}
          {pending && pending.action === 'approve' && (
            <CommentModal
              title={`Aprobar: ${pending.job.topic}`}
              placeholder="Comentario opcional para el registro..."
              confirmLabel="Aprobar"
              onCancel={() => setPending(null)}
              onConfirm={confirmPending}
            />
          )}
          {pending && pending.action === 'changes' && (
            <CommentModal
              title={`Solicitar cambios: ${pending.job.topic}`}
              placeholder="Explica qué debería cambiar..."
              confirmLabel="Solicitar cambios"
              onCancel={() => setPending(null)}
              onConfirm={confirmPending}
            />
          )}
          {pending && pending.action === 'reject' && (
            <CommentModal
              title={`Rechazar: ${pending.job.topic}`}
              placeholder="Motivo del rechazo (opcional)"
              confirmLabel="Rechazar"
              onCancel={() => setPending(null)}
              onConfirm={confirmPending}
            />
          )}
          {hardDeleteConfirm && (
            <ConfirmModal
              title="Eliminación definitiva"
              body="Esta acción no se puede deshacer. Se borrarán el trabajo y su historial de auditoría permanentemente. ¿Continuar?"
              danger
              confirmLabel="Sí, eliminar definitivamente"
              onCancel={() => setHardDeleteConfirm(null)}
              onConfirm={hardDelete}
            />
          )}
          {legacyHardDelete && (
            <ConfirmModal
              title="Eliminación definitiva (legacy)"
              body="Este trabajo del historial legacy se borrará permanentemente. ¿Continuar?"
              danger
              confirmLabel="Sí, eliminar definitivamente"
              onCancel={() => setLegacyHardDelete(null)}
              onConfirm={legacyHardDeleteConfirm}
            />
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default WorkCenterPanel;
